import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Sparkles, Plus, Calculator, BarChart3, Cpu, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { quantAIProvidersAvailable } from '../../lib/api';
import AIExecutionDialog from './AIExecutionDialog';

/**
 * Toolbar AI con 4 azioni rapide:
 *  - 🪄 Migliora con AI  (propose-evolution, in-app)
 *  - 🆕 Crea nuova       (generate-strategy-note, in-app)
 *  - 🧮 Simula           (link al tab Simulatore)
 *  - 📊 Analizza         (apre PromptBuilder con tutti i template)
 *
 * Flusso Migliora/Crea: prima si apre un mini-dialog "intent input" dove l'utente
 * può scrivere una direzione testuale opzionale (es: "voglio stop più stretti").
 * Poi parte AIExecutionDialog passando il testo come custom_text.
 *
 * Props:
 *  - strategy, asset: contesto attivo
 *  - currentSpec, comparison: spec e comparison per popolare il context
 *  - onSimulateClick: () => void → switcha al tab Simulatore
 *  - onOpenAnalyzer: () => void → apre PromptBuilder
 *  - onComplete: (result) => void → chiamato dopo successo, parent ricarica dati
 */
const AIActionToolbar = ({ strategy, asset, currentSpec, comparison, onSimulateClick, onOpenAnalyzer, onComplete }) => {
  const [providers, setProviders] = useState([]);
  const [generatorId, setGeneratorId] = useState(null);
  const [validatorId, setValidatorId] = useState(null);
  const [execDialog, setExecDialog] = useState(null);
  // Intent dialog: { mode: 'migliora' | 'crea', text: string }
  const [intentDialog, setIntentDialog] = useState(null);

  useEffect(() => {
    quantAIProvidersAvailable()
      .then(r => {
        const items = (r.data.items || []).filter(p => p.active);
        setProviders(items);
        if (items.length && !generatorId) {
          setGeneratorId(items[0].id);
          // Default validator: usa il secondo provider se esiste (cross-validation),
          // altrimenti stesso del generator (self-check)
          setValidatorId(items[1]?.id || items[0].id);
        }
      })
      .catch(() => setProviders([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasProvider = !!generatorId;
  const hasStrategyAndAsset = !!strategy && !!asset;

  const buildContext = () => ({
    strategy,
    asset,
    diagnosis: 'baseline',
    previous_versions: (comparison?.previous_versions || []).map(p => p.name),
    competitors: (comparison?.competitors || []).map(c => c.name),
    tickers: currentSpec?.universe || [],
  });

  const openMiglioraIntent = () => {
    if (!hasStrategyAndAsset) {
      toast.warning('Seleziona prima strategia e asset');
      return;
    }
    setIntentDialog({ mode: 'migliora', text: '' });
  };

  const openCreaIntent = () => {
    setIntentDialog({ mode: 'crea', text: '' });
  };

  const launchFromIntent = () => {
    const { mode, text } = intentDialog;
    const customText = (text || '').trim() || null;
    if (mode === 'migliora') {
      setExecDialog({
        title: '🪄 Migliora strategia',
        args: {
          template: 'propose-evolution',
          context: buildContext(),
          custom_text: customText,
          generator_provider_id: generatorId,
          validator_provider_id: validatorId,
          min_score: 50,
        },
      });
    } else {
      setExecDialog({
        title: '🆕 Crea nuova strategia',
        args: {
          template: 'generate-strategy-note',
          context: { strategy: strategy || 'new-strategy', asset: asset || null },
          custom_text: customText,
          generator_provider_id: generatorId,
          validator_provider_id: validatorId,
          min_score: 50,
        },
      });
    }
    setIntentDialog(null);
  };

  if (!providers.length) {
    return (
      <div className="rounded-lg border border-dashed bg-amber-50/30 dark:bg-amber-950/10 p-3 text-xs text-muted-foreground flex items-center gap-2">
        <Cpu className="w-4 h-4 text-amber-600" />
        Nessun provider AI configurato. Vai in{' '}
        <a href="/admin/ai-providers" className="text-primary hover:underline">/admin/ai-providers</a>{' '}
        per aggiungerne uno.
      </div>
    );
  }

  // Suggerimenti contestuali per la textarea intent
  const intentPlaceholder = intentDialog?.mode === 'migliora'
    ? `Es: "Voglio stop più stretti a 1.5x ATR" · "Aggiungi filtro trend a 200gg" · "Includi anche QQQ nell'universe" · "Riduci le whipsaw nel regime laterale"`
    : `Es: "Strategia momentum su crypto con stop tight 14gg" · "Mean reversion su settori SPDR con lookback 5gg" · "Volatility breakout su VIX-correlated assets"`;
  const intentTitle = intentDialog?.mode === 'migliora'
    ? `🪄 Migliora ${strategy || ''}`
    : '🆕 Crea nuova strategia';
  const intentDescription = intentDialog?.mode === 'migliora'
    ? `Dai all'AI una direzione specifica per la variante. Lascia vuoto per una proposta libera basata solo sulla diagnosi.`
    : 'Descrivi che tipo di strategia vuoi generare. Lascia vuoto per una proposta libera basata sul contesto attuale.';

  return (
    <>
      <div className="rounded-lg border bg-card p-3 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
            <Cpu className="w-3.5 h-3.5" /> Azioni AI rapide
          </p>
          <div className="flex items-center gap-2 text-[10px]">
            <Select value={generatorId || ''} onValueChange={setGeneratorId}>
              <SelectTrigger className="h-7 text-[11px] w-48">
                <span className="text-muted-foreground mr-1">gen:</span>
                <SelectValue placeholder="Generator" />
              </SelectTrigger>
              <SelectContent>
                {providers.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} <span className="text-muted-foreground text-[10px] ml-1">({p.model})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={validatorId || ''} onValueChange={setValidatorId}>
              <SelectTrigger className="h-7 text-[11px] w-48">
                <span className="text-muted-foreground mr-1">val:</span>
                <SelectValue placeholder="Validator" />
              </SelectTrigger>
              <SelectContent>
                {providers.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    {p.id === generatorId && <Badge variant="outline" className="ml-1 text-[8px]">self</Badge>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Button
            variant="default"
            className="h-auto py-3 flex-col gap-1"
            onClick={openMiglioraIntent}
            disabled={!hasProvider || !hasStrategyAndAsset}
            title={!hasStrategyAndAsset ? 'Seleziona strategia e asset' : 'Genera una variante evolutiva della strategia'}
          >
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-medium">🪄 Migliora con AI</span>
            <span className="text-[9px] text-primary-foreground/80">propose-evolution</span>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-3 flex-col gap-1"
            onClick={openCreaIntent}
            disabled={!hasProvider}
            title="Crea una nuova strategia da zero, opzionalmente con direzione testuale"
          >
            <Plus className="w-4 h-4" />
            <span className="text-xs font-medium">🆕 Crea nuova</span>
            <span className="text-[9px] text-muted-foreground">generate-strategy-note</span>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-3 flex-col gap-1"
            onClick={onSimulateClick}
            disabled={!hasStrategyAndAsset}
            title="Vedi quanto sarebbero diventati X€ in questa strategia"
          >
            <Calculator className="w-4 h-4" />
            <span className="text-xs font-medium">🧮 Simula</span>
            <span className="text-[9px] text-muted-foreground">backtest custom</span>
          </Button>

          <Button
            variant="outline"
            className="h-auto py-3 flex-col gap-1"
            onClick={onOpenAnalyzer}
            disabled={!hasProvider}
            title="Apri PromptBuilder con tutti i template"
          >
            <BarChart3 className="w-4 h-4" />
            <span className="text-xs font-medium">📊 Analizza</span>
            <span className="text-[9px] text-muted-foreground">tutti i template</span>
          </Button>
        </div>
      </div>

      {/* Intent input dialog: l'utente scrive cosa vuole prima di lanciare l'AI */}
      <Dialog open={!!intentDialog} onOpenChange={(v) => { if (!v) setIntentDialog(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              {intentTitle}
            </DialogTitle>
            <DialogDescription>{intentDescription}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              autoFocus
              value={intentDialog?.text || ''}
              onChange={(e) => setIntentDialog(d => d ? { ...d, text: e.target.value } : null)}
              placeholder={intentPlaceholder}
              className="min-h-[120px] text-sm"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  launchFromIntent();
                }
              }}
            />
            <div className="rounded-md bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground space-y-1">
              <p className="font-medium text-foreground/80">Contesto auto-iniettato (visibile all'AI):</p>
              {intentDialog?.mode === 'migliora' ? (
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Strategia base: <code className="font-mono">{strategy}</code> · asset <code className="font-mono">{asset}</code></li>
                  {currentSpec?.universe?.length > 0 && (
                    <li>Universe attuale: {currentSpec.universe.length} ticker</li>
                  )}
                  {comparison?.previous_versions?.length > 0 && (
                    <li>Versioni precedenti note: {comparison.previous_versions.length}</li>
                  )}
                  {comparison?.competitors?.length > 0 && (
                    <li>Competitor noti: {comparison.competitors.length}</li>
                  )}
                </ul>
              ) : (
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Generator: {providers.find(p => p.id === generatorId)?.name || '?'}</li>
                  <li>Validator: {providers.find(p => p.id === validatorId)?.name || '?'} {validatorId === generatorId && '(self-check)'}</li>
                  {strategy && <li>Strategia di partenza (riferimento): <code className="font-mono">{strategy}</code></li>}
                  {asset && <li>Asset di riferimento: <code className="font-mono">{asset}</code></li>}
                </ul>
              )}
              <p className="text-[10px] mt-1.5 opacity-70">💡 Cmd/Ctrl + Enter per lanciare</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIntentDialog(null)}>
              Annulla
            </Button>
            <Button onClick={launchFromIntent}>
              <ArrowRight className="w-4 h-4 mr-1.5" />
              Genera con AI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {execDialog && (
        <AIExecutionDialog
          open={!!execDialog}
          onOpenChange={(v) => { if (!v) setExecDialog(null); }}
          title={execDialog.title}
          executeArgs={execDialog.args}
          onSuccess={(result) => {
            // Propaga al parent così la pagina ricarica strategie/specs/serie
            try { onComplete?.(result); } catch (_) { /* no-op */ }
          }}
        />
      )}
    </>
  );
};

export default AIActionToolbar;
